import { memo } from 'react';
import { Tooltip, Typography, Badge } from '@mui/material';
import { UserAvatar } from 'components/UserAvatar/UserAvatar';
import { formatAuthorData } from 'utility/functions';
import { PopperPropsExtended } from 'components/IconLabelFields/IconLabelTextField';
import { EditorialAssetSearchResultItem } from 'utility/types/dataProvider';

type Author = {
  name?: string;
  email?: string;
  [key: string]: any;
};

type AuthorsFieldProps = {
  record: EditorialAssetSearchResultItem;
  avatarMode?: boolean;
  lastModified?: boolean;
};

const AuthorsField = memo(
  ({ record, avatarMode = false, lastModified = false }: AuthorsFieldProps) => {
    const { authors = [], last_modified_by } = record || {};

    if (lastModified) {
      // Handle last_modified_by as a single object, not an array
      if (!last_modified_by) return null;

      const displayName = formatAuthorData(last_modified_by, {
        includeEmail: true,
        fallback: '',
      });

      if (avatarMode) {
        return (
          <UserAvatar
            user={last_modified_by}
            size={50}
            placement="top"
            sx={{ mx: 'auto' }}
          />
        );
      }

      return (
        <Typography
          data-private={true}
          sx={() => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          })}
        >
          {displayName}
        </Typography>
      );
    } else {
      // Handle authors as an array
      const authorsLength = authors.length;

      if (authorsLength === 0) return null;

      const firstAuthor = authors[0];
      const firstAuthorDisplayName = formatAuthorData(firstAuthor, {
        includeEmail: true,
        fallback: '',
      });

      const authorsList =
        authorsLength > 1 ? (
          <>
            {authors.map((author: Author, index: number) => {
              const displayName = formatAuthorData(author, {
                includeEmail: true,
                fallback: '',
              });
              return (
                <Typography
                  variant="caption"
                  key={index}
                  data-private={true}
                  sx={{
                    display: "block"
                  }}
                >
                  {displayName}
                </Typography>
              );
            })}
          </>
        ) : null;

      if (avatarMode) {
        return (
          <Badge
            color="info"
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={authorsLength > 1 ? authorsLength : null}
          >
            <UserAvatar
              user={firstAuthor}
              size={50}
              placement="top"
              sx={{ mx: 'auto' }}
              customTitle={authorsList}
            />
          </Badge>
        );
      }

      // Text mode (default)
      return (
        <Tooltip
          slotProps={{
            popper: {
              'data-private': true,
            } as PopperPropsExtended,
          }}
          title={authorsList || ''}
          placement="top"
          arrow
        >
          <Typography
            data-private={true}
            sx={() => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            })}
          >
            {firstAuthorDisplayName}
            {authorsLength > 1 && <span style={{ fontSize: 12 }}>({authorsLength})</span>}
          </Typography>
        </Tooltip>
      );
    }
  },
);

export default AuthorsField;
